/** Treatment program offers, matching evvy-ai's ProgramOffer (app/content/commerce_program.py). */

import type { CommerceCardBlock } from "@/services/commerceOffers";

// At most twelve integer digits and two decimals, no sign, exponent or leading zero.
const MONEY_PATTERN = /^(?:0|[1-9][0-9]{0,11})(?:\.[0-9]{1,2})?$/;

type OneTimeLine = { quantity: 1; is_subscription: false };
export type ProgramItem = OneTimeLine & {
  item_type: "bundle-treatment";
  treatment_product_id: number;
};
export type RetestItem = OneTimeLine & {
  item_type: "product";
  product_id: number;
};
type ProgramAction<Item> = { type: "add_to_cart"; items: Item[] };

export interface ProgramProduct {
  treatment_product_id: number;
  product_slug: string;
  name: string;
  description: string;
  image_url: string | null;
  category: "TREAT" | "PROTECT" | "RELIEF";
  provider_determined: boolean;
  subscribed: boolean;
  // Already a subscription line in the cart: left out of the price and kept as it is, but
  // still in the action, because the program batch must name it (S12).
  in_cart_subscription?: boolean;
}

export interface ProgramRetest {
  name: string;
  description: string;
  image_url: string | null;
  product_slug: "VMT001" | "VMTEXP001";
  price: string;
  full_price: string;
  // Quoted totals for the program with the retest, never for client addition.
  program_price: string;
  program_full_price: string;
  action: ProgramAction<ProgramItem | RetestItem>;
}

export interface ProgramOffer {
  offer_id: "treatment-program";
  kind: "program";
  product_slug: "ctp";
  name: string;
  description: string;
  image_url: string | null;
  pdp_slug: "ctp";
  price: string;
  full_price: string;
  purchase_mode: "one_time";
  perks: string[];
  products: ProgramProduct[];
  action: ProgramAction<ProgramItem>;
  retest: ProgramRetest | null;
}

export type ProgramCardBlock = Omit<CommerceCardBlock, "offer"> & {
  offer: ProgramOffer;
};

/** What a restored program shows: no actions, product IDs or stale prices. Keeps its
 * kind, so a decoded program decodes again (ChatMessage and U7 re-read history). */
export type SavedProgramOffer = Pick<
  ProgramOffer,
  | "offer_id"
  | "kind"
  | "name"
  | "description"
  | "image_url"
  | "pdp_slug"
  | "perks"
> & { products: Omit<ProgramProduct, "treatment_product_id">[] };

/** A program card restored from history. Its own type, so it never reaches a live card. */
export interface SavedProgramCardBlock {
  type: "saved_program_card";
  offer: SavedProgramOffer;
}

type JsonObject = Record<string, unknown>;

const isObject = (value: unknown): value is JsonObject =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isNullableString = (value: unknown): value is string | null =>
  value === null || typeof value === "string";

// Counts code points, as evvy-ai's length limits do.
const isText = (value: unknown, min: number, max: number): value is string =>
  typeof value === "string" &&
  [...value].length >= min &&
  [...value].length <= max;

const isId = (value: unknown): boolean =>
  Number.isSafeInteger(value) && (value as number) > 0;

// Integer cents, so totals compare exactly. NaN when malformed: every comparison then fails.
export const cents = (value: unknown): number => {
  if (typeof value !== "string" || !MONEY_PATTERN.test(value)) return NaN;
  const [whole, fraction = ""] = value.split(".");
  return Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
};

const isLine = (item: unknown, itemType: string, idKey: string): boolean =>
  isObject(item) &&
  item.item_type === itemType &&
  isId(item[idKey]) &&
  item.quantity === 1 &&
  item.is_subscription === false;

const isProgramItem = (item: unknown): item is ProgramItem =>
  isLine(item, "bundle-treatment", "treatment_product_id");

const actionItems = (action: unknown): unknown[] =>
  isObject(action) &&
  action.type === "add_to_cart" &&
  Array.isArray(action.items)
    ? action.items
    : [];

const isProductDisplay = (product: unknown): product is JsonObject =>
  isObject(product) &&
  typeof product.product_slug === "string" &&
  /^[a-z0-9-]+$/.test(product.product_slug) &&
  isText(product.name, 1, 200) &&
  isText(product.description, 0, 2000) &&
  isNullableString(product.image_url) &&
  ["TREAT", "PROTECT", "RELIEF"].includes(product.category as string) &&
  typeof product.provider_determined === "boolean" &&
  typeof product.subscribed === "boolean" &&
  ["undefined", "boolean"].includes(typeof product.in_cart_subscription) &&
  // A subscribed product is outside the action, so it is never a cart line to keep.
  !(product.subscribed && product.in_cart_subscription);

/** The display fields a live program shares with one restored from history. */
const isProgramDisplay = (offer: JsonObject): boolean =>
  offer.kind === "program" &&
  offer.offer_id === "treatment-program" &&
  isText(offer.name, 1, 200) &&
  isText(offer.description, 0, 2000) &&
  isNullableString(offer.image_url) &&
  offer.pdp_slug === "ctp" &&
  Array.isArray(offer.perks) &&
  offer.perks.length <= 8 &&
  offer.perks.every((perk) => typeof perk === "string") &&
  Array.isArray(offer.products) &&
  offer.products.length >= 1 &&
  offer.products.length <= 12 &&
  offer.products.every(isProductDisplay);

/** Only a program's display fields, or null when malformed. Reads B2's history projection. */
export function decodeSavedProgramOffer(
  offer: unknown
): SavedProgramOffer | null {
  if (!isObject(offer) || !isProgramDisplay(offer)) return null;
  const {
    offer_id,
    kind,
    name,
    description,
    image_url,
    pdp_slug,
    perks,
    products,
  } = offer as unknown as ProgramOffer;
  return {
    offer_id,
    kind,
    name,
    description,
    image_url,
    pdp_slug,
    perks,
    // Drops the product IDs even when a live-shaped offer was saved.
    products: products.map(({ treatment_product_id, ...display }) => display),
  };
}

/** A retest whose quoted totals and lines extend the base program exactly. */
function isRetest(
  retest: unknown,
  baseIds: unknown[],
  price: number,
  fullPrice: number
): boolean {
  if (!isObject(retest)) return false;
  const retestPrice = cents(retest.price);
  const retestFullPrice = cents(retest.full_price);
  const items = actionItems(retest.action);
  const treatments = items.filter(isProgramItem);
  const retests = items.filter((item) => isLine(item, "product", "product_id"));
  return (
    isText(retest.name, 1, 200) &&
    isText(retest.description, 0, 2000) &&
    isNullableString(retest.image_url) &&
    ["VMT001", "VMTEXP001"].includes(retest.product_slug as string) &&
    retestFullPrice >= retestPrice &&
    cents(retest.program_price) === price + retestPrice &&
    cents(retest.program_full_price) === fullPrice + retestFullPrice &&
    // The base lines in their order, plus exactly one retest line.
    retests.length === 1 &&
    items.length === treatments.length + 1 &&
    treatments.map((item) => item.treatment_product_id).join() ===
      baseIds.join()
  );
}

/** The live program, or null unless every field, total and purchase set holds. */
export function decodeProgramOffer(offer: unknown): ProgramOffer | null {
  if (!isObject(offer) || !isProgramDisplay(offer)) return null;
  const products = offer.products as JsonObject[];
  const ids = products.map((product) => product.treatment_product_id);
  const unsubscribedIds = products
    .filter((product) => !product.subscribed)
    .map((product) => product.treatment_product_id);
  const items = actionItems(offer.action);
  const itemIds = items.map(
    (item) => isObject(item) && item.treatment_product_id
  );
  const price = cents(offer.price);
  const fullPrice = cents(offer.full_price);
  const valid =
    offer.product_slug === "ctp" &&
    offer.purchase_mode === "one_time" &&
    fullPrice >= price &&
    ids.every(isId) &&
    new Set(ids).size === ids.length &&
    // Exactly the unsubscribed products, once each: subscribed ones are never re-added.
    unsubscribedIds.length > 0 &&
    items.every(isProgramItem) &&
    items.length === unsubscribedIds.length &&
    new Set(itemIds).size === items.length &&
    itemIds.every((id) => unsubscribedIds.includes(id)) &&
    (offer.retest === null ||
      isRetest(offer.retest, itemIds, price, fullPrice));
  return valid ? (offer as unknown as ProgramOffer) : null;
}
